import React from 'react';
import shimbun4 from '../assets/shimbun4.png';
import shimbun5 from '../assets/shimbun5.png';
import shimbun6 from '../assets/shimbun6.png';
import shimbun7 from '../assets/shimbun7.png';
import './CSS/Interview.css';

function Interview2() {
  return (
    <main className="interview-page">
      <div className="interview-masthead">
        Thai Aikikai Exclusive Interview &nbsp;·&nbsp; Exclusive Interview &nbsp;·&nbsp; Special Feature
      </div>

      {/* HERO */}
      <section className="interview-hero">
        <img
          className="interview-hero-img"
          src={shimbun4}
          alt="Fukakusa Shihan interview"
        />

        <div className="interview-hero-overlay"></div>

        <div className="interview-hero-content">
          <div className="interview-hero-label">
            Exclusive Interview · Aikido Special Feature
          </div>

          <h1 className="interview-hero-title">
            The Secret Behind
            <br />
            Aikido&apos;s <em>"Gentleness"</em>
          </h1>

          <p className="interview-hero-subtitle">
            When movements that appear like poetry actually conceal the gravity
            of survival and the geometry of the blade.
          </p>

          <div className="interview-hero-byline">
            <div className="interview-hero-avatar">
              <img src={shimbun4} alt="Fukakusa Shihan" />
            </div>

            <div className="interview-hero-byline-text">
              <strong>Fukakusa Shihan</strong>
              Aikido Master · Interview by Kenichi Ishioka
            </div>
          </div>
        </div>
      </section>

      <div className="interview-article-wrap">
        <div className="interview-intro-block">
          <p>
            To the general public, Aikido is often remembered as a peaceful
            martial art — an attacker rushes in, is turned, guided, and falls
            gently onto the mat as if the movement were a dance. Yet behind that
            flowing gentleness lies the architecture of evading a samurai blade,
            where a single moment can decide survival.
          </p>
        </div>

        {/* SECTION 1 */}
        <div className="interview-section-header">
          <div className="interview-section-num">01</div>
          <div className="interview-section-title-wrap">
            <span>Core Concept</span>
            <h2>The Myth of Gentleness</h2>
          </div>
        </div>

        <div className="interview-clearfix">
          <figure className="interview-portrait-float">
            <img
              src={shimbun5}
              alt="Fukakusa Shihan demonstrating hand structure"
            />
            <figcaption>
              Fukakusa Shihan demonstrates the structure of the hand — a simple
              gesture that conceals the entire calculation of movement.
            </figcaption>
          </figure>

          <div className="interview-conversation">
            <div className="interview-exchange">
              <div className="interview-speaker-tag q">
                <span>Question</span>
              </div>
              <div className="interview-exchange-text">
                Most people probably imagine Aikido as a peaceful martial art,
                almost like a form of dance, don&apos;t they?
              </div>
            </div>

            <div className="interview-exchange">
              <div className="interview-speaker-tag a">
                <span>Shihan</span>
              </div>
              <div className="interview-exchange-text">
                Yes. The image of an attacker rushing in, being turned, and
                falling gently onto the mat looks gentle and yielding. But that
                is what I call <strong>"the myth of gentleness."</strong>
              </div>
            </div>

            <div className="interview-exchange">
              <div className="interview-speaker-tag q">
                <span>Question</span>
              </div>
              <div className="interview-exchange-text">
                So it is not as gentle as it appears?
              </div>
            </div>

            <div className="interview-exchange">
              <div className="interview-speaker-tag a">
                <span>Shihan</span>
              </div>
              <div className="interview-exchange-text">
                Most people watch Aikido and think we do not use force at all.
                But in reality, those movements that look like flowing water are
                supported by elements that are <strong>decisive and sharp</strong>{' '}
                and require extremely precise calculation. If the structure of
                Aikido lacks <strong>Atemi</strong> and <strong>Ma-ai</strong>,
                what remains is only a beautiful dance that cannot truly be
                applied.
              </div>
            </div>
          </div>
        </div>

        <div className="interview-pull-quote">
          <p>
            "If the structure of Aikido lacks Atemi and Ma-ai, what remains is
            only a beautiful dance without real resistance."
          </p>
          <cite>— Fukakusa Shihan</cite>
        </div>

        <div className="interview-concept-card">
          <span className="interview-concept-jp">Atemi</span>
          <h3>Striking — A Tactical Tool</h3>
          <p>
            Atemi is not a punch intended to knock someone out like in boxing.
            It is a strike toward vulnerable targets, such as the eyes or the
            bridge of the nose, used to trigger the body&apos;s automatic
            reflexes. In that instant, weight shifts toward the heels and
            balance begins to collapse.
          </p>
        </div>

        <div className="interview-concept-card">
          <span className="interview-concept-jp">Ma-ai</span>
          <h3>Distance — The Sphere of Power</h3>
          <p>
            Ma-ai is not merely the measurement of how far two people stand from
            each other. It is the awareness of the sphere of power surrounding
            both bodies. This distance is dynamic, constantly shifting from
            moment to moment. Without correct Ma-ai, Atemi has no meaning.
          </p>
        </div>

        {/* SECTION 2 */}
        <div className="interview-section-header">
          <div className="interview-section-num">02</div>
          <div className="interview-section-title-wrap">
            <span>Hidden Mechanics</span>
            <h2>Switching the Brain with Atemi</h2>
          </div>
        </div>

        <div className="interview-conversation">
          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              What if the attacker is very large and trained to endure pain?
              Would a strike that is not meant to knock him out only make him
              angrier?
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              Atemi does not attack the muscles to cause damage. It targets{' '}
              <strong>automatic reflexes</strong> — the eyes, the bridge of the
              nose, the throat. No matter how large the attacker is, human
              beings cannot resist the instinctive response to blink or recoil.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              That is true. If something rushes toward the eyes at high speed,
              everyone instinctively pulls back.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              Exactly. When that recoil happens, the chin rises, the spine
              arches slightly, and the body&apos;s weight shifts toward the
              heels without the person realizing it. In that fraction of a
              second, balance collapses. It is in that narrow transitional
              moment that a throw or lock can enter.
            </div>
          </div>
        </div>

        <div className="interview-pull-quote">
          <p>
            "We are not trying to lift the whole rock. We find the wedge that
            breaks its balance, then let it roll downhill by itself."
          </p>
          <cite>— Kenichi Ishioka, Interviewer</cite>
        </div>

        {/* SECTION 3 */}
        <div className="interview-section-header">
          <div className="interview-section-num">03</div>
          <div className="interview-section-title-wrap">
            <span>The Hidden Root</span>
            <h2>The Geometry of the Blade</h2>
          </div>
        </div>

        <div className="interview-conversation">
          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              Why does Aikido movement differ from boxing, which often
              emphasizes slipping and countering in a straight line? Is it
              connected to weapons?
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              Correct. The secret behind Aikido&apos;s circular movement is its
              inseparable relationship with <strong>swordsmanship</strong>,
              especially Kenjutsu. The body mechanics, stepping, hip rotation,
              and hand angles used in empty-hand practice are actually the same
              structure as when holding a katana.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              It is like learning to drive a manual transmission first, so that
              you understand the engine more deeply. Even when driving an
              automatic car later, your understanding is deeper than someone who
              started with automatic from the beginning.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              That is a very good comparison. If someone rushes in with an
              empty-hand punch, you might raise your arm and block it. At worst,
              the muscle is bruised. But if what is coming toward you is the
              edge of a katana, blocking directly with the arm could mean
              serious injury or death. The science of the sword therefore
              forced people to discover how to move off the line, redirect the
              path of the blade, and use circular movement instead of receiving
              force directly.
            </div>
          </div>
        </div>

        {/* TWO-UP IMAGES */}
        <div className="interview-img-row">
          <figure>
            <img src={shimbun6} alt="Fukakusa Shihan practicing jo" />
            <figcaption>
              Jo practice — the stepping and hip rotation share the same
              structure as empty-hand Aikido.
            </figcaption>
          </figure>

          <figure>
            <img src={shimbun7} alt="Fukakusa Shihan in sword posture" />
            <figcaption>
              Sword posture in the dojo — beneath the portrait of O-Sensei, the
              founder of Aikido.
            </figcaption>
          </figure>
        </div>

        <div className="interview-pull-quote">
          <p>
            "The movement we see is a simulation of surviving an unseen samurai
            blade — a thin line between life and death, compressed into the
            space of the dojo."
          </p>
          <cite>— Fukakusa Shihan</cite>
        </div>

        {/* SECTION 4 */}
        <div className="interview-section-header">
          <div className="interview-section-num">04</div>
          <div className="interview-section-title-wrap">
            <span>Philosophy of Practice</span>
            <h2>Kihon — The Only Filter of Mastery</h2>
          </div>
        </div>

        <div className="interview-conversation">
          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              If modern practitioners only train empty-hand techniques and never
              pick up the wooden sword, do they risk losing the original spirit
              of the art?
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              There is a very high possibility of that. Without understanding
              the root of the sword, Aikido movement can appear unreasonable.
              But the deeper problem is skipping steps. Many people see the
              fluidity of O-Sensei and try to imitate it by jumping over{' '}
              <strong>Kihon</strong>, the basic forms.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag q">
              <span>Question</span>
            </div>
            <div className="interview-exchange-text">
              Like a music student who sees a legendary jazz pianist improvise
              freely, then tries to imitate it by pressing random keys, even
              though they still cannot play basic scales in rhythm.
            </div>
          </div>

          <div className="interview-exchange">
            <div className="interview-speaker-tag a">
              <span>Shihan</span>
            </div>
            <div className="interview-exchange-text">
              The result is only noise without direction. Kihon often must be
              repeated thousands or tens of thousands of times. It can feel
              stiff, unnatural, and sometimes painful. But true freedom of
              movement only arises when the body and brain absorb discipline so
              deeply that it becomes{' '}
              <strong>instinct that no longer requires thought</strong>.
            </div>
          </div>
        </div>

        <div className="interview-ornament">— · · · —</div>

        <div className="interview-closing-box">
          <h3>Final Reflection: Ask Yourself</h3>

          <p>
            If an art like Aikido, which appears gentle, refined, and
            conciliatory, is actually built upon sharp logic, danger, and the
            discipline of swordsmanship, then look again at the systems around
            you. What organizations, relationships, or structures in your own
            life appear flexible and gentle on the surface, but are actually
            supported by rules and discipline as strong as steel?
          </p>

          <p className="interview-closing-note">
            "The gentleness we see from the outside is not a symbol of weakness.
            It is <span>powerful gentleness</span>, because it is the result of a
            deep understanding of the structure and psychology of combat." —
            Fukakusa Shihan
          </p>
        </div>
      </div>

      <footer className="interview-footer">
        <strong>Thai Aikikai</strong>
        <br />
        This interview was adapted and edited from an original conversation from
        the YouTube channel Aikido Renbukan BKK.
      </footer>
    </main>
  );
}

export default Interview2;
